import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { useCallback } from 'react'
import { loginRequest, isEntraConfigured, apiScopes } from './msalConfig'

export interface AuthState {
  isAuthenticated: boolean
  isEntraEnabled: boolean
  isLoading: boolean
  user: {
    name: string
    email: string
  } | null
  login: () => Promise<void>
  logout: () => Promise<void>
  getAccessToken: () => Promise<string | null>
}

// Hook for authentication state and actions
export function useAuth(): AuthState {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const isEntraEnabled = isEntraConfigured()
  const account = accounts[0] ?? null

  // Get user info from the active account
  const user = account
    ? {
        name: account.name ?? account.username ?? 'User',
        email: account.username ?? '',
      }
    : null

  // Login with redirect (navigates away to Entra ID, returns with auth code)
  const login = useCallback(async () => {
    if (!isEntraEnabled) return
    await instance.loginRedirect(loginRequest)
  }, [instance, isEntraEnabled])

  // Logout with redirect
  const logout = useCallback(async () => {
    if (!isEntraEnabled) return
    await instance.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/login`,
    })
  }, [instance, isEntraEnabled])

  // Get access token for API calls
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!isEntraEnabled || !account) return null

    try {
      const response = await instance.acquireTokenSilent({
        scopes: [apiScopes.read].filter(Boolean),
        account,
      })
      return response.accessToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expired or consent required - trigger interactive login via redirect
        await instance.acquireTokenRedirect({
          scopes: [apiScopes.read].filter(Boolean),
        })
        return null
      }
      console.error('Token acquisition failed:', error)
      return null
    }
  }, [instance, account, isEntraEnabled])

  return {
    isAuthenticated: isEntraEnabled ? isAuthenticated : false,
    isEntraEnabled,
    isLoading: inProgress !== 'none',
    user,
    login,
    logout,
    getAccessToken,
  }
}

// Hook that provides just the token acquisition function (for api.ts)
export function useAccessToken(): () => Promise<string | null> {
  const { getAccessToken } = useAuth()
  return getAccessToken
}
